import produce from "immer";
import { AnyAction, Reducer } from "redux";
import { createReducer, handleSimpleReducerUpdates, ReducerDictionary } from "common/ReduxUtils";
import { BillingActionTypes, CREDITS_PER_PAGE } from "./BillingActions";
import { BillingSummary } from "common/clients/billing/BillingSummary";
import { CustomerInvoiceHistory } from "common/clients/billing/CustomerInvoiceHistory/CustomerInvoiceHistory";
import {
  Credit,
  SellerConfigDataResponse,
  SellerDueDataResponse,
  SellerDueToleranceValuePast30DaysType,
  UnbilledChargesResponse,
} from "@deliverr/billing-types";
import { PaymentSource } from "common/clients/billing/PaymentSource";
import { ContraRevenueInvoiceHistory } from "common/clients/billing/Credit/ContraRevenueInvoiceHistory";
import { DEFAULT_TOLERANCE_VALUE } from "./constants";

export interface BillingReducerState {
  session?: any;
  summary: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: BillingSummary;
  };
  invoices: {
    loading: boolean;
    error: boolean;
    data: CustomerInvoiceHistory[];
    currentPage: number;
  };
  credits: {
    nextPage: number;
    hasNextPage: boolean;
    loading: boolean;
    error: boolean;
    data: Credit[];
  };
  primaryPaymentSource: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: PaymentSource;
  };
  contraRevenue: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: ContraRevenueInvoiceHistory;
  };
  sellerConfig: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: SellerConfigDataResponse;
  };
  sellerDues: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: SellerDueDataResponse;
  };
  unbilledCharges: {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    data: UnbilledChargesResponse;
  };
}

export const billingInitialState: BillingReducerState = {
  summary: {
    loading: false,
    error: false,
    loaded: false,
    data: {
      dueNow: 0,
      creditBalance: 0,
      hasCreditHistory: false,
      nextInvoiceDate: undefined,
      totalInvoiceCount: 0,
      unpaidInvoiceCount: 0,
      sellerId: "",
      unbilledCharges: {
        charges: [],
        totalAmount: 0,
        lookbackPeriod: 0,
      },
    },
  },
  invoices: {
    loading: false,
    error: false,
    data: [],
    currentPage: 0,
  },
  credits: {
    nextPage: 0,
    hasNextPage: true,
    loading: false,
    error: false,
    data: [],
  },
  primaryPaymentSource: {
    loading: false,
    error: false,
    loaded: false,
    data: {},
  },
  contraRevenue: {
    loading: false,
    error: false,
    loaded: false,
    data: {
      creditNotesAppliedToInvoice: [],
      creditsAppliedToInvoice: [],
    },
  },
  sellerConfig: {
    loading: false,
    error: false,
    loaded: false,
    data: {
      autoSuspensionEnabled: false,
      dueToleranceValuePast30Days: DEFAULT_TOLERANCE_VALUE,
      dueToleranceValuePast30DaysType: SellerDueToleranceValuePast30DaysType.ABSOLUTE,
      sellerId: "",
      updatedBy: {
        sub: "",
        iss: "",
      },
    },
  },
  sellerDues: {
    loading: false,
    error: false,
    loaded: false,
    data: {
      id: 0,
      sellerId: "",
      totalBalance: 0,
      balanceDue: 0,
      balanceDuePast30Days: 0,
      lastComputedAt: new Date(),
      createdAt: new Date(),
      updatedAt: new Date(),
    },
  },
  unbilledCharges: {
    loading: false,
    error: false,
    loaded: false,
    data: {
      charges: [],
      totalAmount: 0,
      lookbackPeriod: 0,
    },
  },
};

const reducers: ReducerDictionary<BillingReducerState> = {
  ...handleSimpleReducerUpdates(["SET_CHARGEBEE_SESSION"]),
  [BillingActionTypes.FETCH_BILLING_SUMMARY_START]: (state) =>
    produce(state, (draft) => {
      draft.summary.loading = true;
    }),
  [BillingActionTypes.FETCH_BILLING_SUMMARY_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.summary.loading = false;
      draft.summary.error = false;
      draft.summary.loaded = true;
      draft.summary.data = payload;
    }),
  [BillingActionTypes.FETCH_BILLING_SUMMARY_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.summary.loading = false;
      draft.summary.error = true;
    }),
  [BillingActionTypes.FETCH_INVOICES_START]: (state) =>
    produce(state, (draft) => {
      draft.invoices.loading = true;
    }),
  [BillingActionTypes.FETCH_INVOICES_SUCCESS]: (state, { payload: { invoices, page } }) =>
    produce(state, (draft) => {
      draft.invoices.loading = false;
      draft.invoices.error = false;
      draft.invoices.data = invoices;
      draft.invoices.currentPage = page;
    }),
  [BillingActionTypes.FETCH_INVOICES_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.invoices.loading = false;
      draft.invoices.error = true;
    }),
  [BillingActionTypes.FETCH_CREDITS_START]: (state) =>
    produce(state, (draft) => {
      draft.credits.loading = true;
    }),
  [BillingActionTypes.FETCH_CREDITS_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      const credits = payload as Credit[];

      draft.credits.data.push(...credits);
      draft.credits.nextPage += 1;
      draft.credits.hasNextPage = credits.length === CREDITS_PER_PAGE;
      draft.credits.loading = false;
      draft.credits.error = false;
    }),
  [BillingActionTypes.FETCH_CREDITS_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.credits.loading = false;
      draft.credits.error = true;
    }),
  [BillingActionTypes.RESET_BILLING]: () => billingInitialState,
  [BillingActionTypes.FETCH_PRIMARY_PAYMENT_SOURCE_START]: (state) =>
    produce(state, (draft) => {
      draft.primaryPaymentSource.loading = true;
    }),
  [BillingActionTypes.FETCH_PRIMARY_PAYMENT_SOURCE_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.primaryPaymentSource.loading = false;
      draft.primaryPaymentSource.error = false;
      draft.primaryPaymentSource.loaded = true;
      draft.primaryPaymentSource.data = payload;
    }),
  [BillingActionTypes.FETCH_PRIMARY_PAYMENT_SOURCE_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.primaryPaymentSource.loading = false;
      draft.primaryPaymentSource.error = true;
    }),
  [BillingActionTypes.FETCH_CONTRA_REVENUE_START]: (state) =>
    produce(state, (draft) => {
      draft.contraRevenue.loading = true;
      draft.contraRevenue.data = {
        creditsAppliedToInvoice: [],
        creditNotesAppliedToInvoice: [],
      };
    }),
  [BillingActionTypes.FETCH_CONTRA_REVENUE_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.contraRevenue.loading = false;
      draft.contraRevenue.error = false;
      draft.contraRevenue.loaded = true;
      draft.contraRevenue.data = payload;
    }),
  [BillingActionTypes.FETCH_CONTRA_REVENUE_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.contraRevenue.loading = false;
      draft.contraRevenue.error = true;
    }),
  [BillingActionTypes.FETCH_SELLER_CONFIG_START]: (state) =>
    produce(state, (draft) => {
      draft.sellerConfig.loading = true;
    }),
  [BillingActionTypes.FETCH_SELLER_CONFIG_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.sellerConfig.loading = false;
      draft.sellerConfig.error = false;
      draft.sellerConfig.loaded = true;
      draft.sellerConfig.data = payload.value;
    }),
  [BillingActionTypes.FETCH_SELLER_CONFIG_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.sellerConfig.loading = false;
      draft.sellerConfig.error = true;
    }),
  [BillingActionTypes.FETCH_SELLER_DUES_START]: (state) =>
    produce(state, (draft) => {
      draft.sellerDues.loading = true;
    }),
  [BillingActionTypes.FETCH_SELLER_DUES_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.sellerDues.loading = false;
      draft.sellerDues.error = false;
      draft.sellerDues.loaded = true;
      draft.sellerDues.data = payload.value;
    }),
  [BillingActionTypes.FETCH_SELLER_DUES_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.sellerDues.loading = false;
      draft.sellerDues.error = true;
    }),
  [BillingActionTypes.FETCH_UNBILLED_CHARGES_START]: (state) =>
    produce(state, (draft) => {
      draft.unbilledCharges.loading = true;
    }),
  [BillingActionTypes.FETCH_UNBILLED_CHARGES_SUCCESS]: (state, { payload }) =>
    produce(state, (draft) => {
      draft.unbilledCharges.loading = false;
      draft.unbilledCharges.error = false;
      draft.unbilledCharges.loaded = true;
      draft.unbilledCharges.data = payload;
    }),
  [BillingActionTypes.FETCH_UNBILLED_CHARGES_ERROR]: (state) =>
    produce(state, (draft) => {
      draft.unbilledCharges.loading = false;
      draft.unbilledCharges.error = true;
    }),
};

export const billingReducer: Reducer<BillingReducerState, AnyAction> = createReducer<BillingReducerState>(
  billingInitialState,
  reducers
);
